import classNames from "classnames";
import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import Table from "components/DesignSystem/Table/V2/Table";
import { ClockCountdown } from "components/icons/ClockCountdown";
import { DeleteIcon } from "components/icons/delete";
import { EmptyCart } from "components/TPPayments/Illustration/EmptyCart";
import { CART_ITEM_STATUS } from "constants/cart";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { useModal } from "hooks/useModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { flexRender, getCoreRowModel, useReactTable } from "react-table-8.10.7";
import WarningCircle from "static/images/WarningCircle.svg";
import {
  Cart as CartT,
  productsApi,
  useCheckoutCartMutation,
  useGetCartQuery,
  useGetCartItemsQuery,
  useRemoveProductFromCartMutation,
  useUpdateCartItemMutation,
  CartItem,
} from "store/apis/products";
import { refferalApis, useGetCreditsQuery } from "store/apis/refrral";
import { subscriptionApis } from "store/apis/subscriptions";
import { taskApis } from "store/apis/task";
import { ArrayElement } from "types/utils/ArrayElement";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import {
  cartColumn,
  createCartColumn,
  QuantityColumn,
} from "./CartTableColumn";
import { SaveForLaterCard } from "./SaveForLaterCard";
import { MobileCartItemRow } from "./MobileCartItemRow";
import { ArrowLeft } from "components/icons/ArrowLeft";
import React from "react";
import Async from "components/DesignSystem/AsyncComponents/Async";
import ToolTip from "components/design/toolTip";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { useAnalytics } from "hooks/useAnalytics";
import { MOVE_TO_SAVE_FOR_LATER } from "constants/analyticsEvents";
import { Postpaid } from "components/icons/Postpaid";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { DONT_NEED, FILED_EXTERNALLY } from "constants/cartItemDeletionReasons";
import { CartItemDeletionReason } from "types/contants/cartItemDeletionReasonTypes";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { CART_ITEM_DELETION_REASON_DICT } from "constants/cartItemDeletionReasonsDict";
import { BellIcon } from "components/icons/BellIcon";
import { useNotifyCartMutation } from "store/apis/chat";
import { TotalDueToday } from "components/CheckoutModal/TotalDueToday";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { TaxSubscriptionUpdate } from "components/billing/TaxSubscriptionUpdate/TaxSubscriptionUpdate";
import { CartPlanConvertUI } from "./CartPlanConvertUI";
import { useAuth } from "hooks/useAuth";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { InfoBlue } from "components/icons/InfoBlue";
import { DISPLAY_DATE } from "constants/date";
import { ConditionalLink } from "components/conditionalLink";
dayjs.extend(isSameOrBefore);

export type CartTableMeta = {
  entityId: string;
  groupId: string;
};

export const PERIOD_MAP = {
  MONTHLY: "month",
  QUARTERLY: "quarter",
  YEARLY: "year",
};

export const CartItemActions = ({
  cartItem,
  entityId,
}: {
  cartItem: ArrayElement<CartT["cart_items"]>;
  entityId: string;
}) => {
  const { trackEvent } = useAnalytics();
  const { successToast, alertToast } = useToast();
  const group = useCurrentGroup();
  const groupId = group.uuid;
  const [removeCartItem, { isLoading: isRemoving }] =
    useRemoveProductFromCartMutation();
  const [saveForLater, { isLoading: isSaving }] = useUpdateCartItemMutation();
  const [deleteReason, setDeletionReason] =
    useState<CartItemDeletionReason>(DONT_NEED);
  const deleteModal = useModal();
  const { isAdmin } = useRoleBasedView();

  const onDelete = async () => {
    if (groupId && cartItem) {
      try {
        await removeCartItem({
          groupId,
          cartItemId: cartItem.uuid,
          entityId: entityId,
          reason: deleteReason,
        }).unwrap();
        successToast({ message: "Item has been deleted!" });
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  };

  const onSaveForLater = async () => {
    try {
      await saveForLater({
        entityId,
        payload: {
          cartItemId: cartItem.uuid,
          status: CART_ITEM_STATUS.SAVED_FOR_LATER,
        },
      }).unwrap();
      trackEvent(MOVE_TO_SAVE_FOR_LATER, {
        cart_item_id: cartItem.uuid,
      });
      successToast({ message: "Item has been moved to Save for later" });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const saveForLaterinDeleteModal = () => {
    onSaveForLater();
    deleteModal.close();
  };

  const onDeleteReasonClick = (reason: CartItemDeletionReason) => {
    deleteModal.open();
    setDeletionReason(reason);
  };

  return (
    <div className="t-flex t-justify-end">
      <Dropdown.Root>
        <ConditionalToolTip
          condition={!isAdmin && "Please contact support to modify your cart"}
        >
          <Dropdown.Trigger asChild disabled={!isAdmin}>
            <span>
              <Button
                isLoading={isRemoving}
                disabled={!isAdmin}
                customType="ghost_icon"
                size="small"
              >
                <span className="t-text-text-30">
                  <DeleteIcon stroke="1.5" />
                </span>
              </Button>
            </span>
          </Dropdown.Trigger>
        </ConditionalToolTip>
        <Dropdown.Content>
          <Dropdown.Item onSelect={() => onDeleteReasonClick(DONT_NEED)}>
            {" "}
            I don't need this filing
          </Dropdown.Item>
          <Dropdown.Item onSelect={() => onDeleteReasonClick(FILED_EXTERNALLY)}>
            I filed it externally
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Root>
      <ConditionalToolTip condition={!isAdmin && "Save for later"}>
        <span>
          <Button
            isLoading={isSaving}
            customType="ghost_icon"
            size="small"
            onClick={onSaveForLater}
          >
            <span className="t-text-text-30">
              <ClockCountdown />
            </span>
          </Button>
        </span>
      </ConditionalToolTip>
      <Modal.Root open={deleteModal.isOpen} onOpenChange={deleteModal.close}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Delete This Filing?</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <p className="t-mb-4 t-text-body-lg t-text-text-60">
              Are you sure you want to delete this from your cart and mark this
              '{CART_ITEM_DELETION_REASON_DICT[deleteReason]}'?
            </p>
            <Banner
              type="info"
              description="Not sure about it yet? Save it for later instead—you'll find it in your cart when you're ready."
            />
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={saveForLaterinDeleteModal}>Save for later</Button>
            <Button
              customType="danger"
              onClick={onDelete}
              isLoading={isRemoving}
              disabled={isRemoving}
            >
              Delete{" "}
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </div>
  );
};

export const CartModal = ({
  open,
  onClose,
  entityIdFromProps,
  groupIdFromProps,
  shouldRedirectToTask = true,
  handlePaymentSuccess,
  hideEntitySelector = false,
  addQuery,
}: {
  open: boolean;
  onClose: () => void;
  entityIdFromProps?: string;
  groupIdFromProps?: string;
  shouldRedirectToTask?: boolean;
  handlePaymentSuccess?: () => void;
  hideEntitySelector?: boolean;
  addQuery?: boolean;
}) => {
  const group = useCurrentGroup();
  const query = useQuery();
  const highlightTemplate = query.get("from_template");
  const groupId = groupIdFromProps || group.uuid;
  const { update } = useUpdateQuery();
  const taxSubscriptionUpdateModal = useModal();
  const { platform_subscription: groupPlatformSubscription } =
    useCurrentGroupContext();
  const { platform_subscription: customerSubscription } = useAuth();

  const [checkoutCart, { isLoading: isCheckoutLoading, data: cartInvoice }] =
    useCheckoutCartMutation();

  const [notifyCart, { isLoading }] = useNotifyCartMutation();

  const history = useHistory();
  const [isPayViaCredits, setIsPayViaCredits] = useState(false);
  const [showInsufficentError, setShowInsufficentError] = useState(false);

  const dispatch = useAppDispatch();
  const { isMobile } = useWindowSize();
  const entityUuid = useCurrentEntityId();
  const { isAdmin, isCustomer, isForeignCA, isPersonalTaxUser } =
    useRoleBasedView();
  const entityId = entityIdFromProps || entityUuid;

  const { data: cart, ...cartApiState } = useGetCartQuery(
    { groupId: groupId!, entityId: entityId! },
    { skip: !groupId || !entityId }
  );

  const { data: saveForLaterCart, ...saveForLaterCartApiState } =
    useGetCartItemsQuery(
      { entityId, status: CART_ITEM_STATUS.SAVED_FOR_LATER },
      {
        skip: !entityId,
      }
    );

  const { successToast, alertToast } = useToast();

  const isCartLoading =
    saveForLaterCartApiState.isLoading || cartApiState.isLoading;
  const isCartRefreshing =
    saveForLaterCartApiState.isFetching || cartApiState.isFetching;
  const isSuccess =
    saveForLaterCartApiState.isSuccess && cartApiState.isSuccess;
  const isEmpty =
    isSuccess &&
    Boolean(cart) &&
    cart?.cart_items.length === 0 &&
    Boolean(saveForLaterCart) &&
    saveForLaterCart?.cart_items.length === 0;

  const totalSubscriptionCredit = cart?.total_subscription_credit_amount || 0;

  const isPostpaid = (item: CartItem) => {
    return (
      item.product_details.base_task_key === "US_BOOKKEEPING_ONE_TIME" &&
      (isNaN(Number(item.subtotal)) || Number(item.subtotal) === 0)
    );
  };

  const isPostpaidIncluded = cart?.cart_items.some((item) => isPostpaid(item));
  const isOnlyPostpaidRequired = cart?.cart_items.every((item) =>
    isPostpaid(item)
  );

  const { data: creditsData } = useGetCreditsQuery(
    { groupId: groupId!, is_cart_payment: true },
    { skip: !groupId }
  );

  const { total_credits } = creditsData || {};

  const onNotify = async () => {
    if (groupId && entityId) {
      try {
        await notifyCart({ entityId }).unwrap();
        successToast({ message: "Client has been notified!" });
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  };

  const onPaymentSuccess = async () => {
    if (groupId) {
      dispatch(productsApi.util.invalidateTags([{ type: "CART" }]));
      dispatch(taskApis.util.invalidateTags([{ type: "Tasks" }]));
      dispatch(
        subscriptionApis.util.invalidateTags([{ type: "Subscriptions" }])
      );
      dispatch(refferalApis.util.invalidateTags([{ type: "CREDITS" }]));

      closePayment();
      onClose();
      handlePaymentSuccess?.();
      if (shouldRedirectToTask) {
        history.push("/filings?check_for_tasks=true");
      } else if (addQuery)
        update({
          query: "check_for_tasks",
          value: true,
        });
    }
  };

  const columns = [
    cartColumn.name(),
    QuantityColumn(),
    cartColumn.season(),
    cartColumn.rate(),
    cartColumn.total(),
    createCartColumn.display({
      header: "",
      id: "actions",
      cell: (info) => {
        return (
          <CartItemActions
            cartItem={info.row.original}
            // @ts-ignore
            entityId={info.table.options.meta.entityId}
          />
        );
      },
      size: 5,
    }),
  ];

  const table = useReactTable({
    data: cart?.cart_items || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 10 },
    meta: {
      entityId: cart?.entity_id || entityId!,
    },
  });

  const {
    isOpen: isPaymentOpen,
    close: closePayment,
    open: openPayment,
  } = useModal();

  const onCheckout = async () => {
    if (groupId && cart?.uuid) {
      try {
        await checkoutCart({
          groupId,
          cartId: cart.uuid,
          entityId: cart.entity_id,
        }).unwrap();
        openPayment();
      } catch (error) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  };

  const handleCreditCheckboxChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (cart && cart.order_summary.total <= total_credits!) {
      await checkoutCart({
        groupId: groupId!,
        cartId: cart.uuid,
        entityId: cart.entity_id,
      }).unwrap();
      setIsPayViaCredits(e.target.checked);
    } else {
      if (!e.target.checked) setShowInsufficentError(false);
      else setShowInsufficentError(true);
    }
  };

  const groupTaxSubscriptionId = isCustomer
    ? customerSubscription.subscription_uuid
    : groupPlatformSubscription.subscription_uuid;

  const cartTitle = (
    <Modal.Title>
      Your Cart{" "}
      {cart && cart.order_summary.items_count > 0 && (
        <>({pluralize(cart.order_summary.items_count, "item", "items")})</>
      )}
    </Modal.Title>
  );

  const mobileHeader = (
    <div className="t-flex t-items-center t-gap-2">
      <Modal.RawClose asChild>
        <Button customType="ghost_icon" size="small" aria-label="Back">
          <ArrowLeft size="16px" />
        </Button>
      </Modal.RawClose>
      {cartTitle}
    </div>
  );

  const desktopHeader = (
    <>
      {cartTitle}
      <Modal.Close />
    </>
  );

  return (
    <>
      <Modal.Root open={open} onOpenChange={onClose} modal={false}>
        <Modal.Content size="xxl" useCustomOverlay>
          <Modal.Header className="sm:t-px-4">
            {isMobile ? mobileHeader : desktopHeader}
          </Modal.Header>
          <Modal.Body className="!t-pt-0 t-mt-5 sm:t-px-4">
            {!hideEntitySelector && (
              <div
                className={classNames("t-w-52 t-mb-5 sm:t-w-full", {
                  "t-invisible": isPersonalTaxUser,
                })}
              >
                <EntitySelector
                  size="small"
                  showOnlyUSEntities
                  toolTipPlacement="top"
                />
              </div>
            )}
            <Async.Root
              isLoading={isCartLoading}
              isEmpty={isEmpty}
              isSuccess={isSuccess}
              customLoader={
                <div className="t-flex t-justify-center t-items-center t-flex-col t-gap-3">
                  <Loader size="small" />
                </div>
              }
            >
              <Async.Empty>
                <div className="t-flex t-justify-center t-items-center t-flex-col t-gap-4">
                  <EmptyCart />
                  <div className="t-flex t-flex-col t-items-center t-gap-1.5">
                    <p className="t-text-text-100 t-text-subtitle-sm t-m-0">
                      Your cart is empty
                    </p>
                    <p className="t-text-text-30 t-text-body-sm t-m-0 t-text-center">
                      Browse filings, bookkeeping and other services to get
                      started.
                    </p>
                  </div>
                  {isCustomer && (
                    <ConditionalLink
                      to={`/settings/services?entity=${entityId}`}
                    >
                      <Button
                        customType="secondary"
                        size="small"
                        onClick={onClose}
                      >
                        Browse Products & Services
                      </Button>
                    </ConditionalLink>
                  )}
                </div>
              </Async.Empty>
              <Async.Success>
                <div className="t-flex t-gap-6 sm:t-flex-col">
                  <div className="t-w-8/12 sm:t-w-full t-flex t-flex-col t-gap-5">
                    {(cart?.cart_items.length || 0) > 0 ||
                    (saveForLaterCart?.cart_items ?? []).length > 0 ? (
                      <div className="t-max-h-96 t-overflow-y-auto sm:t-max-h-none sm:t-overflow-visible t-flex t-flex-col t-gap-5">
                        <div className="t-flex t-flex-col sm:t-gap-4">
                          {cart && cart.cart_items.length > 0 && (
                            <Table.Container className="t-w-full sm:t-rounded-lg sm:t-border sm:t-border-solid sm:t-border-neutral-0 sm:t-px-4">
                              <Table.Content className="t-w-full">
                                <Table.Head className="sm:t-hidden">
                                  {table
                                    .getHeaderGroups()
                                    .map((headerGroup) => (
                                      <Table.Row key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                          <Table.HeadCell
                                            key={header.id}
                                            className="t-text-subtext-sm t-uppercase t-py-2"
                                            style={{
                                              width: `${header.getSize()}%`,
                                            }}
                                          >
                                            {header.isPlaceholder
                                              ? null
                                              : flexRender(
                                                  header.column.columnDef
                                                    .header,
                                                  header.getContext()
                                                )}
                                          </Table.HeadCell>
                                        ))}
                                      </Table.Row>
                                    ))}
                                </Table.Head>
                                <Table.Body>
                                  {table.getRowModel().rows.map((row) => {
                                    const {
                                      product_id,
                                      base_task_key,
                                      from_date,
                                    } = row.original.product_details || {};
                                    const showTaxSubscriptionTransition =
                                      product_id ===
                                        process.env
                                          .PUBLIC_TAX_YEARLY_SUBSCRIPTION_ID &&
                                      groupTaxSubscriptionId ===
                                        process.env
                                          .PUBLIC_TAX_MONHTLY_SUBSCRIPTION_ID;

                                    const billingStartDate = from_date
                                      ? dayjs(from_date)
                                      : null;

                                    const isBookkeepingV2 =
                                      row.original.subscription
                                        ?.subscription_type ===
                                      "BOOKKEEPING_V2";

                                    return (
                                      <>
                                        <Table.Row
                                          key={row.id}
                                          className={classNames(
                                            "sm:t-border-dashed sm:last:t-border-b-0",
                                            {
                                              "t-border-b-0":
                                                showTaxSubscriptionTransition ||
                                                billingStartDate,
                                              "t-bg-blue-10":
                                                highlightTemplate ===
                                                base_task_key,
                                            }
                                          )}
                                        >
                                          {isMobile ? (
                                            <td
                                              colSpan={columns.length}
                                              className="t-py-3"
                                            >
                                              <MobileCartItemRow row={row} />
                                            </td>
                                          ) : (
                                            row
                                              .getVisibleCells()
                                              .map((cell) => (
                                                <td
                                                  key={cell.id}
                                                  style={{
                                                    width: `${cell.column.getSize()}%`,
                                                  }}
                                                  className="t-py-4 t-px-2"
                                                >
                                                  {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                  )}
                                                </td>
                                              ))
                                          )}
                                        </Table.Row>{" "}
                                        {billingStartDate &&
                                          isBookkeepingV2 && (
                                            <Table.Row className="t-p-0">
                                              <Table.Cell
                                                colSpan={columns.length}
                                                className="!t-p-0"
                                              >
                                                <div className="t-flex t-items-start t-gap-x-1">
                                                  <InfoBlue className="t-size-3 t-mt-0.5" />
                                                  <div className="t-w-full t-pb-3 t-text-text-30 t-text-body-sm t-italic">
                                                    This amount will not be
                                                    deducted now, the recurring
                                                    billing starts on{" "}
                                                    {billingStartDate.format(
                                                      DISPLAY_DATE
                                                    )}
                                                    .
                                                  </div>
                                                </div>
                                              </Table.Cell>
                                            </Table.Row>
                                          )}
                                        {showTaxSubscriptionTransition && (
                                          <Table.Row className="t-p-0">
                                            <Table.Cell
                                              colSpan={columns.length}
                                              className="!t-p-0"
                                            >
                                              <div className="t-w-full t-mb-3.5 t-p-3.5 t-rounded-2xl t-bg-purple-5 t-border t-border-solid t-border-purple-10">
                                                <div className="t-text-body-sm t-text-text-60 t-flex t-gap-1">
                                                  We're sunsetting the Inkle Tax
                                                  monthly plan and transitioning
                                                  you to annual billing.{" "}
                                                  <Button
                                                    size="small"
                                                    customType="link"
                                                    onClick={
                                                      taxSubscriptionUpdateModal.open
                                                    }
                                                  >
                                                    Learn more
                                                  </Button>
                                                </div>
                                                <CartPlanConvertUI />
                                              </div>
                                            </Table.Cell>
                                          </Table.Row>
                                        )}
                                      </>
                                    );
                                  })}
                                </Table.Body>
                              </Table.Content>
                            </Table.Container>
                          )}
                          {Boolean(saveForLaterCart) &&
                            (saveForLaterCart?.cart_items ?? []).length > 0 && (
                              <SaveForLaterCard
                                entityId={entityId}
                                groupId={groupId!}
                              />
                            )}
                        </div>
                      </div>
                    ) : null}
                    <TaxSubscriptionUpdate {...taxSubscriptionUpdateModal} />
                  </div>

                  {cart && (
                    <div className="t-flex t-flex-col t-w-4/12 sm:t-w-full t-gap-y-2.5">
                      <div className="t-p-4 t-w-full t-bg-surface-lighter-grey t-border-solid t-border t-border-neutral-0 t-rounded t-max-h-fit t-flex t-flex-col">
                        <p className="t-pb-4 t-border-solid t-border-b t-border-neutral-0 t-border-0 t-text-subtext">
                          Payment Summary
                        </p>
                        <div className="t-mb-4">
                          <p className="t-flex t-m-0 t-justify-between t-text-body-sm t-mr-2">
                            <span>
                              Subtotal (
                              {pluralize(
                                cart.order_summary.items_count || 0,
                                "item",
                                "items"
                              )}
                              )
                            </span>
                            <div className="t-flex t-flex-col t-items-end">
                              {isOnlyPostpaidRequired ? (
                                <div className="t-text-body-sm t-text-text-60">
                                  Postpaid
                                </div>
                              ) : (
                                <span>
                                  <AmountSuperScript
                                    amount={cart.order_summary.subtotal}
                                  />
                                </span>
                              )}
                              {isPostpaidIncluded &&
                                !isOnlyPostpaidRequired && (
                                  <div className="t-text-body- t-text-text-30">
                                    + Postpaid
                                  </div>
                                )}
                            </div>
                          </p>

                          {Boolean(cart.order_summary.discount) &&
                            (cart.order_summary.discount?.discount_value || 0) >
                              0 && (
                              <p className="t-flex t-m-0 t-justify-between t-text-body-sm t-mt-4">
                                <span>
                                  Discount (
                                  <span className="t-text-dark_green">
                                    {
                                      cart.order_summary.discount?.coupon
                                        ?.coupon_code
                                    }
                                  </span>
                                  )
                                </span>
                                <span className="t-text-dark_green">
                                  -
                                  <AmountSuperScript
                                    amount={
                                      cart.order_summary.discount
                                        ?.discount_value || 0
                                    }
                                  />
                                </span>
                              </p>
                            )}

                          {totalSubscriptionCredit < 0 && (
                            <div className="t-flex t-justify-between t-text-body-sm t-mt-4">
                              <div className="t-space-y-0.5">
                                <span className="t-text-dark_green t-text-body-sm">
                                  Old subscription credit
                                </span>
                                <div className="t-text-body-sm t-text-text-30">
                                  We have credited your existing monthly payment
                                  towards your annual plan.
                                </div>
                              </div>

                              <span className="t-text-dark_green">
                                <AmountSuperScript
                                  amount={totalSubscriptionCredit}
                                />
                              </span>
                            </div>
                          )}

                          {isAdmin && (
                            <div className="t-mt-4">
                              <Checkbox
                                label={"Pay using Inkle credits"}
                                onChange={handleCreditCheckboxChange}
                              />
                              <div className="t-text-body-sm t-pl-6 t-flex t-flex-col t-gap-0.5">
                                <span className="t-text-neutral-40 t-flex t-gap-1 t-items-center">
                                  {total_credits?.toString()} credits available
                                </span>
                                {showInsufficentError && (
                                  <span className="t-flex t-items-center t-gap-1">
                                    <span className="t-text-red">
                                      Insufficient credit balance{" "}
                                    </span>
                                    <img
                                      src={WarningCircle}
                                      alt="Warning circle"
                                      className="t-h-3.5"
                                    />
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <p className="t-flex t-m-0 t-justify-between t-text-subtext t-pt-4 t-border-solid t-border-0 t-border-t t-border-neutral-0 t-mt-8 t-mr-2">
                          <span className="t-text-text-60">Cart total</span>
                          <div className="t-flex t-flex-col t-items-end">
                            {isOnlyPostpaidRequired ? (
                              <div className="t-text-subtitle t-text-text-60">
                                Postpaid
                              </div>
                            ) : (
                              <span>
                                <AmountSuperScript
                                  amount={cart.order_summary.total}
                                />
                              </span>
                            )}
                            {isPostpaidIncluded && !isOnlyPostpaidRequired && (
                              <div className="t-text-body-sm t-text-text-30">
                                + Postpaid
                              </div>
                            )}
                          </div>
                        </p>
                        {isPostpaidIncluded && (
                          <div className="t-flex t-items-baseline t-gap-1.5 t-mt-4">
                            <span className="t-text-text-30">
                              <Postpaid />
                            </span>
                            <div className="t-text-body-sm t-text-text-30">
                              Postpaid charges are paid after the service has
                              been completed
                            </div>
                          </div>
                        )}
                      </div>
                      <TotalDueToday
                        total={cart.order_summary?.total}
                        totalDueToday={cart.order_summary?.total_due_today}
                      />
                    </div>
                  )}
                </div>
              </Async.Success>
            </Async.Root>
          </Modal.Body>
          <Modal.Footer className="sm:t-px-4">
            <div className="t-flex t-justify-between t-w-full t-items-center">
              {isCustomer && !isEmpty && (
                <ConditionalLink to={`/settings/services?entity=${entityId}`}>
                  <Button customType="ghost" size="small" onClick={onClose}>
                    + Products & Services
                  </Button>
                </ConditionalLink>
              )}
              <div className="t-flex t-gap-3 t-ml-auto">
                {isAdmin ? (
                  <Button
                    onClick={onNotify}
                    leftIcon={<BellIcon />}
                    isLoading={isLoading}
                  >
                    Notify client
                  </Button>
                ) : (
                  <Button onClick={onClose}>Close</Button>
                )}
                {isPayViaCredits && cart ? (
                  <CheckoutModal
                    type="cart"
                    onCartPaid={onPaymentSuccess}
                    cart={{
                      ...cart,
                      cart_items: cart.cart_items.map((item) =>
                        item.subscription
                          ? {
                              ...item,
                              subscription: {
                                ...item.subscription,
                                selectedTierAmount:
                                  item.tier_information?.amount,
                                raStateName: item.ra_state_name,
                                salesTaxStateName: item.sales_tax_state_name,
                                cartItemId: item.uuid,
                              },
                            }
                          : item
                      ),
                    }}
                    cartInvoice={cartInvoice?.invoice}
                    title="Cart Payment"
                    onClose={closePayment}
                    open={isPaymentOpen}
                    onlyCTA={isPayViaCredits}
                    useCredits={isPayViaCredits}
                    payerUserType={isForeignCA && "FCA"}
                  />
                ) : (
                  <Button
                    onClick={onCheckout}
                    customType="primary"
                    isLoading={isCheckoutLoading}
                    disabled={
                      isCheckoutLoading ||
                      !cart ||
                      cart.cart_items.length === 0 ||
                      isCartRefreshing ||
                      (showInsufficentError && isPayViaCredits)
                    }
                  >
                    Checkout
                  </Button>
                )}
              </div>
            </div>
          </Modal.Footer>
          {!isPayViaCredits && open && isPaymentOpen && cart && (
            <CheckoutModal
              type="cart"
              onCartPaid={onPaymentSuccess}
              cart={{
                ...cart,
                cart_items: cart.cart_items.map((item) =>
                  item.subscription
                    ? {
                        ...item,
                        subscription: {
                          ...item.subscription,
                          selectedTierAmount: item.tier_information?.amount,
                          raStateName: item.ra_state_name,
                          salesTaxStateName: item.sales_tax_state_name,
                          cartItemId: item.uuid,
                          customPrice: item.unit_price,
                        },
                      }
                    : item
                ),
              }}
              cartInvoice={cartInvoice?.invoice}
              title="Cart Payment"
              onClose={() => {
                closePayment();
                onClose();
              }}
              open={isPaymentOpen}
              payerUserType={isForeignCA && "FCA"}
            />
          )}
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
